import { createContext, type ReactNode } from "react"

import type { Storage } from "../storage/storage"
import { ViewState, type ViewStateValue } from "./view-state"

type InstanceValues = { [instanceId: string]: { [key: string]: ViewStateValue } }
const emptyInstances: InstanceValues = {}

/** Presentation state retained with document tabs; shared values are defaults for new instances. */
export class DocumentViewState {
  private readonly instances: ViewState

  constructor(
    private readonly shared: ViewState,
    storage: Storage,
    storageKey: string
  ) {
    this.instances = new ViewState(storage, storageKey)
  }

  get isReady() {
    return this.shared.isRestored && this.instances.isRestored
  }

  async whenReady() {
    await Promise.all([this.shared.whenRestored(), this.instances.whenRestored()])
  }

  get<T extends ViewStateValue>(instanceId: string, key: string, fallback: T): T {
    const values = this.readInstances()[instanceId]
    return (values != null && Object.hasOwn(values, key) ? values[key] : this.shared.get(key, fallback)) as T
  }

  /** Seed once. Hydration never promotes a restored value to shared defaults. */
  initialize<T extends ViewStateValue>(instanceId: string, key: string, fallback: T) {
    if (!this.isReady) return
    const instances = this.readInstances()
    if (instances[instanceId] != null && Object.hasOwn(instances[instanceId], key)) return
    this.write(instanceId, key, this.shared.get(key, fallback))
  }

  /** An explicit component change promotes its complete value, without merging other instances. */
  set<T extends ViewStateValue>(instanceId: string, key: string, value: T) {
    if (!this.isReady) throw new Error("Document ViewState is still restoring.")
    this.write(instanceId, key, value)
    this.shared.set(key, value)
  }

  subscribe(instanceId: string, key: string, listener: () => void) {
    const unsubscribeInstances = this.instances.subscribe("instances", listener)
    // Readiness can change after instance storage has already restored. Snapshot
    // equality prevents shared changes from rerendering a seeded instance.
    const unsubscribeShared = this.shared.subscribe(key, listener)
    return () => {
      unsubscribeInstances()
      unsubscribeShared()
    }
  }

  /** Called by the workspace, including for restored tabs whose components have never mounted. */
  retainInstances(ids: ReadonlySet<string>) {
    if (!this.isReady) return
    const instances = this.readInstances()
    const retained = Object.fromEntries(Object.entries(instances).filter(([id]) => ids.has(id)))
    if (Object.keys(retained).length !== Object.keys(instances).length) this.instances.set("instances", retained)
  }

  private write(instanceId: string, key: string, value: ViewStateValue) {
    const instances = this.readInstances()
    this.instances.set("instances", {
      ...instances,
      [instanceId]: { ...instances[instanceId], [key]: value },
    })
  }

  private readInstances(): InstanceValues {
    const value = this.instances.get<ViewStateValue>("instances", emptyInstances)
    if (value == null || typeof value !== "object" || Array.isArray(value)) return emptyInstances
    if (Object.values(value).some((fields) => fields == null || typeof fields !== "object" || Array.isArray(fields)))
      return emptyInstances
    return value as InstanceValues
  }
}

export const DocumentViewStateContext = createContext<{ store: DocumentViewState; instanceId: string } | null>(null)

export function DocumentViewStateProvider({
  store,
  instanceId,
  children,
}: {
  readonly store: DocumentViewState
  readonly instanceId: string
  readonly children: ReactNode
}) {
  return <DocumentViewStateContext.Provider value={{ store, instanceId }}>{children}</DocumentViewStateContext.Provider>
}
